import { useEffect, useMemo, useState } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import { useParams } from 'react-router-dom';
import kanbanService from '../../service/kanban.service';
import { useLoader } from '../../provider/LoaderProvider';

import useParam from '../../context/ParamContext';
import useUsers from '../../context/UsersContext';
import {
  ViewType,
  KanbanType,
  conditions,
  KanbanFilterFormState,
  FunnelViewType,
  NavigateUrlToKanbanType,
  addButtonByType,
  KanbanDialog,
} from '../../config/kanban';
import {
  generateAutocompleteItems,
  generateFilter,
  getIcon,
  getKanbanFieldsByControl,
  getKanbanFooterTitle,
} from '../../utils/helper';
import { ColorPicker } from '../../config/ColorPicker';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import Card from './Card';
import {
  AutoCompleteSelectUI,
  ButtonUI,
  DebouncedInputUI,
  FormLabelUI,
  SelectUI,
  SwitchUI,
  TooltipUI,
} from '../Interface';
import Dialog from './Dialog';
import MessageDialog from './MessageDialog';
import masterDataService from '../../service/masterData.service';
import { IconButton } from '@mui/material';
import StatusDialog from '../Status/StatusDialog';
import ColorDialog from './ColorDialog';
import { ProjectPage } from '../Project/ProjectPage';
import { ResourceOnePage } from '../Resources/NewResourceOne/ResourceOnePage';
import { TaskFeedbackPage } from '../TaskFeedback/TaskFeedbackPage';
import { SimpleTaskForm } from '../NewSimpleTask/SimpleTaskForm';
import { useProject } from '../../hooks/useProject';
import { useResourceOne } from '../../hooks/useResourceOne';
import { useTaskFeedback } from '../../hooks/useTaskFeedback';
import { useSimpleTask } from '../../hooks/useSimpleTask';
import AddDialog from './AddDialog';
import TaskListReportDialog from '../TaskListReport/TaskListReportDialog';
import { getResponsibleList } from '../../utils/getResponsibleList';
import { TaskResponsibleTypeEnum } from '../../config/taskList';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import SimpleTaskDeadLineNotificationDialog from '../SimpleTaskDeadLineNotification/SimpleTaskDeadLineNotificationDialog';
import useSimpleTaskDeadLineNotify from '../../context/SimpleTaskDeadLineNotifyContext';

const ComponentMap = {
  [KanbanType.PROJECT]: ProjectPage,
  [KanbanType.RESOURCE]: ResourceOnePage,
  [KanbanType.FEEDBACK]: TaskFeedbackPage,
  [KanbanType.TASK]: SimpleTaskForm,
};

const KanbanView = ({ funnelView }) => {
  const projectState = useProject();
  const resourceOneState = useResourceOne();
  const taskFeedbackState = useTaskFeedback();
  const simpleTaskState = useSimpleTask();
  const { getUser, user, getRole } = useUsers();
  const { subMenuName, projectTypeId } = useParams();
  const { getSetSimpleTaskDeadLineNotify, simpleTaskDeadLineNotify } = useSimpleTaskDeadLineNotify();
  const saveView = useSaveView(user);
  const kanbanFilter = useView('kanbanFilter', user);
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [kanbanData, setKanbanData] = useState([]);
  const [item, setItem] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [filterFormState, setFilterFormState] = useState({});
  const [loading, setLoading] = useState(null);
  const [type, setType] = useState('');
  const [defaultFilterFields, setDefaultFilterFields] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [taskChanged, setTaskChanged] = useState(false);
  const [messageData, setMessageData] = useState({
    addressedTo: null,
    eventData: {},
  });
  const [status, setStatus] = useState({});
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    company: [],
    project: [],
    responsible: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [roles, setRoles] = useState({});
  const [colorDialogOpen, setColorDialogOpen] = useState({ open: false, lineId: null, subMenuUrl: '' });
  const [dialogParams, setDialogParams] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState({ open: false, data: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [responsibleType, setResponsibleType] = useState(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const [query, setQuery] = useState({
    filter: [],
  });

  useEffect(() => {
    const kanbanDialogConfig = KanbanDialog.find((card) => card.type === type);
    const paramName = kanbanDialogConfig?.paramName;

    if (params[paramName]) {
      const parsedParams = JSON.parse(params[paramName]);
      setDialogParams(parsedParams);
    }
  }, [params, type]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(simpleTaskDeadLineNotify).length) getSetSimpleTaskDeadLineNotify();
    getParam();
  }, []);

  useEffect(() => {
    if (!Object.keys(params).length) return;
    if (type !== '') return;

    const defaultFromParam = params.KANBAN_DEFAULT_VIEW;

    setType(defaultFromParam || KanbanType.TASK);
  }, [params, type]);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole([
        'TASK_KANBAN_SEE_ONLY_OWN',
        'TASKLIST_KANBAN_SEE_ONLY_OWN',
        'TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP',
        'TASK_KANBAN_EDIT_DEADLINE_NOTIFICATION',
      ]);

      setRoles(roles);
      initDefaultValues(roles);
    }
  }, [user.userId]);

  useEffect(() => {
    if (subMenuName) {
      const resolvedType = NavigateUrlToKanbanType[subMenuName] || KanbanType.TASK;
      setType(resolvedType);
    }
  }, [subMenuName]);

  useEffect(() => {
    if (subMenuName) return;
    if (!Object.keys(kanbanFilter).length) return;

    const excludedViews = params.KANBAN_EXCLUDE_VIEWS
      ? JSON.parse(params.KANBAN_EXCLUDE_VIEWS).map((e) => e.viewName)
      : [];

    const savedType = funnelView ? kanbanFilter?.funnelViewType : kanbanFilter?.viewType;
    if (!savedType) return;

    const defaultFromParam = params.KANBAN_DEFAULT_VIEW;

    if (excludedViews.includes(savedType)) {
      setType(defaultFromParam || KanbanType.TASK);
      return;
    }

    setType(savedType);
  }, [kanbanFilter, funnelView, subMenuName, params]);

  useEffect(() => {
    if (taskChanged) {
      getItemsByType();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  const kanbanViewAccess =
    params.KANBAN_VIEW_ACCESS && params.KANBAN_VIEW_ACCESS.split(',').map((access) => parseInt(access));

  const loadResponsiblePersonValue = params.KANBAN_RESPONSIBLE_PERSON_DEFAULT;

  const hasAccess = kanbanViewAccess && kanbanViewAccess.includes(user.userId) ? 'Y' : 'N';

  useEffect(() => {
    if (isLoading) return;
    if (!type) return;

    const base = KanbanFilterFormState[type] ?? {};
    const next = { ...base };

    if (type === KanbanType.TASKLIST) {
      if (roles.TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP) {
        const userGroupsForUser = autoCompleteOptions.userGroup.map((group) => group.value);

        next.userGroupId = userGroupsForUser;
        next.responsiblePersonId = user.userId;
      } else if (roles.TASKLIST_KANBAN_SEE_ONLY_OWN) {
        next.responsiblePersonId = user.userId;
      }
    }

    if (type === KanbanType.TASKLIST || type === KanbanType.PROJECT) {
      if (projectTypeId) {
        next.projectTypeId = parseInt(projectTypeId);
      }
    }

    if (
      type === KanbanType.TASK &&
      (loadResponsiblePersonValue === 'Y' || loadResponsiblePersonValue === undefined || roles.TASK_KANBAN_SEE_ONLY_OWN)
    ) {
      next.resposiblePersonId = user.userId;
    }

    setFilterFormState(next);
  }, [type, user.userId, roles, isLoading]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const defaultFilter = [
      {
        field: 'deletedBy',
        value: null,
      },
    ];

    const currentConditions = conditions[type || KanbanType.TASK];
    const newFilter = generateFilter(filterFormState, currentConditions);
    setQuery((prev) => {
      return { ...prev, filter: [...defaultFilter, ...newFilter] };
    });
    setDefaultFilterFields(defaultFilter);
  }, [filterFormState]);

  useEffect(() => {
    if (refetch) {
      getItemsByType();
    }
  }, [refetch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (defaultFilterFields.length > 0 && query.filter.length > 0 && !isLoading) {
        getItemsByType();
      }
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [query, defaultFilterFields.length, isLoading]);

  const refreshKanbanBoard = params.KANBAN_BOARD_TIMED_UPDATE && Number(params.KANBAN_BOARD_TIMED_UPDATE);

  useEffect(() => {
    if (
      query &&
      type &&
      hasAccess &&
      refreshKanbanBoard &&
      typeof refreshKanbanBoard === 'number' &&
      refreshKanbanBoard > 0
    ) {
      const intervalId = setInterval(getItemsByType, refreshKanbanBoard);

      return () => clearInterval(intervalId);
    }
  }, [query, type, hasAccess]);

  const showAddButton = useMemo(() => {
    return addButtonByType.includes(type);
  }, [type]);

  const excludeViews = params.KANBAN_EXCLUDE_VIEWS ? JSON.parse(params.KANBAN_EXCLUDE_VIEWS) : [];

  const filteredViewType = useMemo(() => {
    return ViewType.filter((view) => !excludeViews.some((exclude) => exclude.viewName === view.value));
  }, [excludeViews]);

  const getCurrentViewField = () => {
    if (type === KanbanType.PROJECT) {
      return projectState.viewField;
    } else if (type === KanbanType.RESOURCE) {
      return resourceOneState.viewField;
    } else if (type === KanbanType.FEEDBACK) {
      return taskFeedbackState.viewField;
    } else if (type === KanbanType.TASK) {
      return simpleTaskState.viewField;
    }
  };

  const SpecificComponent = ComponentMap[type] ?? null;

  const initDefaultValues = (rolesParam) => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(getResponsibleList());

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          company: resp[1],
          projectType: resp[2],
          project: resp[3],
          userGroup: resp[4],
          responsible: resp[5],
        };

        const userGroup = rolesParam?.TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP
          ? aData.userGroup.filter((group) => group.userGroupUserLink?.some((link) => link.userId === user.userId))
          : aData.userGroup;

        setAutoCompleteData({
          ...aData,
          userGroup,
        });

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          userGroup: generateAutocompleteItems(userGroup, 'name', 'id'),
          responsible: aData.responsible,
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
        //hideLoader();
      });
  };

  const getStatusByType = () => {
    setStatusDialogOpen(true);
    showLoader();
    kanbanService
      .getStatusByType(type)
      .then((data) => {
        setStatus(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemsByType = () => {
    showLoader();
    kanbanService
      .getItemsByType(type, query, hasAccess)
      .then((data) => {
        setKanbanData(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemById = (id) => {
    showLoader();
    kanbanService
      .getItemById(type, id)
      .then((data) => {
        setItem(data);
        setDialogOpen(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleDropAndStatusUpdate = (statusId, kanbanItem) => {
    setLoading(kanbanItem.id);
    const newStatus = kanbanData.find((kanbanColumn) => kanbanColumn.itemId === statusId);

    const updatedColumn = {
      ...kanbanItem,
      statusId: statusId,
      propertyId: kanbanItem.propertyId ?? null,
      preparedness: newStatus.preparedness ? Number(newStatus.preparedness) : null,
      status: {
        ...kanbanItem.status,
        itemName: newStatus.itemName,
        color: newStatus.color,
        statusId: newStatus.itemId,
      },
    };

    kanbanService
      .updateItemsByType(type, kanbanItem.id, statusId, updatedColumn)
      .then((data) => {
        if (type === KanbanType.TASKLIST) {
          setRefetch(true);
        } else {
          setKanbanData((prev) => {
            return prev.map((kanbanColumn) => {
              if (kanbanColumn.itemId === data.statusId) {
                return {
                  ...kanbanColumn,
                  items: [data, ...kanbanColumn.items.filter((item) => item.id !== kanbanItem.id)],
                };
              } else if (kanbanColumn.itemId === kanbanItem.statusId) {
                return {
                  ...kanbanColumn,
                  items: kanbanColumn.items.filter((item) => item.id !== kanbanItem.id),
                };
              }
              return kanbanColumn;
            });
          });
        }
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const onDragStart = (event, kanbanItem) => {
    event.dataTransfer.setData('kanbanItem', JSON.stringify(kanbanItem));

    if (funnelView) {
      const dragImage = event.target.cloneNode(true);
      dragImage.style.position = 'absolute';
      dragImage.style.opacity = '1';
      dragImage.style.pointerEvents = 'none';
      dragImage.style.transform = 'translateY(-50%)';
      document.body.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, 0, 0);

      setTimeout(() => {
        document.body.removeChild(dragImage);
      }, 0);
    }
  };

  const showMessageDialogParam = params.KANBAN_SHOW_MESSAGE_DIALOG ? JSON.parse(params.KANBAN_SHOW_MESSAGE_DIALOG) : [];

  const showMessageDialog =
    showMessageDialogParam && showMessageDialogParam.find((dialogPr) => dialogPr.kanbanType.includes(type));

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const onDrop = (event, statusId) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const kanbanItem = JSON.parse(event.dataTransfer.getData('kanbanItem'));

    if (kanbanItem.statusId !== statusId) {
      handleDropAndStatusUpdate(statusId, kanbanItem);
      if (!funnelView && showMessageDialog) {
        setMessageDialogOpen(true);
        setMessageData((prev) => ({
          ...prev,
          eventData: kanbanItem,
        }));
      }
    }
  };

  const calcWidth = (index) => {
    if (!funnelView) return 'auto';
    if (index === 0) return 'calc(100% - 30px)';
    return windowWidth < 1400 ? `calc(100% - ${150 * index}px)` : `calc(100% - ${200 * index}px)`;
  };

  const handleTextLikeChange = (fieldName, newValue) => {
    setFilterFormState((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
  };

  const handleAutocompleteChange = (fieldName, newVal, reason, control) => {
    setFilterFormState((prev) => {
      if (control === 'multiAutocomplete') {
        return {
          ...prev,
          [fieldName]: reason === 'clear' ? [] : (newVal ?? []).map((v) => v.value),
        };
      }

      setResponsibleType(null);

      if (fieldName === 'responsible') {
        const fieldByType = {
          [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
          [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
          [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
          [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
        };

        setResponsibleType(newVal?.type ?? null);

        const allFields = Object.values(fieldByType);
        const activeField = fieldByType[newVal?.type];

        return {
          ...prev,
          // minden responsible mező törlése
          ...Object.fromEntries(allFields.map((f) => [f, ''])),

          // csak az aktuális típus kap értéket
          ...(activeField && {
            [activeField]: reason === 'clear' ? '' : newVal?.value ?? '',
          }),
        };
      }

      return {
        ...prev,
        [fieldName]: reason === 'clear' ? '' : newVal?.value ?? '',
      };
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    if (!name) return;

    if (!checked) {
      setFilterFormState((prev) => {
        const updatedState = { ...prev };
        delete updatedState[name];
        return updatedState;
      });
      return;
    }

    setFilterFormState((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  return (
    <div className="bg-gray-100 relative pt-1 w-full px-3 pb-3" style={{ minHeight: 'calc(100vh - 65px)' }}>
      <div className="absolute md:fixed top-[10px]  md:top-1.5 md:right-[170px] mt-1 z-0 md:z-40 ">
        <IconButton onClick={() => getStatusByType()}>
          <SettingsIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
      </div>
      {params.SHOW_KANBAN_TASK_DEADLINE_NOTIFICATION_ICON === 'Y' && roles.TASK_KANBAN_EDIT_DEADLINE_NOTIFICATION && (
        <div className="absolute md:fixed top-[10px]  md:top-1.5 md:right-[200px] mt-1 z-0 md:z-40 ">
          <IconButton onClick={() => setNotificationDialogOpen(true)}>
            <EditNotificationsIcon sx={{ fontSize: '1.8rem' }} />
          </IconButton>
        </div>
      )}
      <div
        className={`md:left-80 z-0 md:z-40  ${funnelView ? 'absolute top-20 md:-top-14 left-5' : 'absolute top-[60px] left-5 md:-top-14'}`}
      >
        <div className="flex flex-col md:flex-row w-full md:gap-3">
          <div className="w-full md:w-[110px]">
            <SelectUI
              label={<FormLabelUI text="Nézet" textColor="md:text-white" />}
              option={funnelView ? FunnelViewType : filteredViewType}
              value={type}
              onChange={(e) => {
                const value = e.target.value;
                setType(value);
                saveView('kanbanFilter', funnelView ? { funnelViewType: value } : { viewType: value });
              }}
            />
          </div>
          <div className="w-[160px]">
            {getKanbanFieldsByControl(type, 'textLike').map(([fieldName, cfg]) => (
              <DebouncedInputUI
                key={fieldName}
                label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                debounceMs={800}
                fieldName={fieldName}
                setQuickFilterSearchValue={handleTextLikeChange}
                quickFilterSearchValue={filterFormState[fieldName]}
              />
            ))}
          </div>
          {getKanbanFieldsByControl(type, 'autocomplete').map(([fieldName, cfg]) => {
            const disabled = cfg.getDisabled(roles);

            if (cfg.shouldShow && !cfg.shouldShow(params)) return null;

            const fieldByType = {
              [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
              [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
              [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
              [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
            };

            const usedFieldName = fieldName === 'responsible' ? fieldByType[responsibleType] : fieldName;

            return (
              <div key={fieldName} className={cfg.widthClass}>
                <AutoCompleteSelectUI
                  variant="standard"
                  onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
                  selectedValue={filterFormState[usedFieldName]}
                  selectedLabelValue={
                    fieldName === 'responsiblePersonId'
                      ? autoCompleteOptions[cfg.optionsKey]?.find(
                          (o) => o.value === filterFormState[usedFieldName] && o.type === responsibleType
                        )?.label
                      : autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === filterFormState[usedFieldName])
                          ?.label
                  }
                  label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                  dataset={autoCompleteOptions[cfg.optionsKey]}
                  disabled={disabled}
                />
              </div>
            );
          })}
          {getKanbanFieldsByControl(type, 'multiAutocomplete').map(([fieldName, cfg]) => {
            const disabled = cfg.getDisabled(roles);

            const selectedValues = (filterFormState[fieldName] ?? []).map((id) =>
              autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === id)
            );

            if (cfg.shouldShow && !cfg.shouldShow(params)) return null;

            return (
              <div key={fieldName} className={cfg.widthClass}>
                <AutoCompleteSelectUI
                  limitTags={1}
                  variant="standard"
                  onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
                  selectedValue={selectedValues}
                  label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                  dataset={autoCompleteOptions[cfg.optionsKey]}
                  disabled={disabled}
                  freeSolo
                  multiple
                />
              </div>
            );
          })}
          {console.log(getKanbanFieldsByControl(type, 'switch'))}
          {getKanbanFieldsByControl(type, 'switch').map(([fieldName, cfg]) => {
            return (
              <div key={fieldName} className={cfg.widthClass}>
                <SwitchUI
                  name={fieldName}
                  checked={filterFormState[fieldName] || false}
                  label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                  onChange={handleSwitchChange}
                />
              </div>
            );
          })}
          <div className="pt-3" onClick={() => setAddDialogOpen(true)}>
            {showAddButton && getIcon('Add', '2rem', 'white')}
          </div>
        </div>
      </div>
      <div className={`${!funnelView ? ' overflow-x-auto w-full custom-scrollbar' : ''}`}>
        <div
          className={`flex flex-col justify-center w-full mt-4 pt-64 md:pt-0 ${funnelView ? 'md:items-center' : 'md:flex-row md:items-start md:flex-gow gap-5'}`}
          style={{ minWidth: !funnelView ? 'max-content' : '' }}
        >
          {kanbanData.map((kanban, index) => {
            const statusColor = ColorPicker.find((col) => col.colorName === kanban.color);
            const secondaryColor = statusColor ?? kanban?.color;
            return (
              <div
                key={kanban.itemId}
                className={`flex ${funnelView ? 'flex-row items-center' : 'flex-col basis-1/4 text-labelColor relative'}`}
                style={{
                  width: calcWidth(index),
                  minWidth: !funnelView ? '350px' : '',
                  maxWidth: !funnelView ? '350px' : '',
                }}
              >
                {funnelView && (
                  <div
                    className="text-center font-bold p-2 min-w-[230px] max-w-[230px] mr-2 relative -left-3 truncate"
                    style={{ color: statusColor ? statusColor?.textColor : secondaryColor }}
                  >
                    <div className="-mt-2 truncate">
                      <TooltipUI title={kanban.itemName} className="cursor-pointer">
                        {kanban.itemName}
                      </TooltipUI>
                    </div>
                    <div>
                      <span className="text-5xl">{kanban.items.length}</span> db
                    </div>
                  </div>
                )}

                <div
                  onDragOver={(event) => onDragOver(event)}
                  onDrop={(event) => onDrop(event, kanban.itemId)}
                  style={{
                    minWidth: funnelView ? `calc(100% - 230px)` : 'auto',
                  }}
                >
                  {!funnelView && (
                    <div
                      className={`${funnelView ? 'py-1' : 'py-4'} pl-2 text-center text-xl font-bold relative`}
                      style={{ borderBottom: `3px solid ${statusColor ? statusColor?.colorCode : secondaryColor}` }}
                    >
                      {kanban.itemName}
                    </div>
                  )}
                  <div
                    className={` overflow-y-auto custom-scrollbar ${funnelView ? 'h-[140px]' : 'w-full h-[350px] pb-10 md:h-screen  p-3'}`}
                    style={{
                      maxHeight: 'calc(100vh - 205px)',
                      backgroundColor: funnelView ? (statusColor ? statusColor?.kanbanBgColor : secondaryColor) : '',
                    }}
                  >
                    <div className={`${funnelView ? 'flex flex-wrap gap-x-2 gap-y-2 p-2' : ''}`}>
                      {kanban.items.map((data) => {
                        return (
                          <Card
                            type={type}
                            key={data.id}
                            data={data}
                            onDragStart={onDragStart}
                            getItemById={getItemById}
                            statusColor={statusColor}
                            secondaryColor={secondaryColor}
                            params={params}
                            loading={loading === data.id ? loading : null}
                            funnelView={funnelView}
                            user={user}
                            setRefetch={setRefetch}
                            setColorDialogOpen={setColorDialogOpen}
                            setReportDialogOpen={setReportDialogOpen}
                            autoCompleteData={autoCompleteData}
                          />
                        );
                      })}
                    </div>
                  </div>
                  {!funnelView && (
                    <div className="absolute z-20 bottom-0 bg-gray-100 w-full py-1 flex justify-center">
                      <p className="font-bold">
                        {getKanbanFooterTitle(type)}: {kanban.items.length}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Dialog
        open={dialogOpen}
        data={item}
        handleClose={() => {
          setDialogOpen(false);
          setItem({});
        }}
        type={type}
        params={params}
        changed={{ taskChanged, setTaskChanged }}
        getCurrentViewField={getCurrentViewField}
        SpecificComponent={SpecificComponent}
        dialogParams={dialogParams}
      />

      <AddDialog
        open={addDialogOpen}
        handleClose={() => {
          setAddDialogOpen(false);
        }}
        changed={{ taskChanged, setTaskChanged }}
        getCurrentViewField={getCurrentViewField}
        SpecificComponent={SpecificComponent}
      />

      <TaskListReportDialog
        open={reportDialogOpen.open}
        handleClose={() => {
          setReportDialogOpen((prev) => ({
            ...prev,
            open: false,
          }));
        }}
        setTaskChanged={setTaskChanged}
        data={reportDialogOpen.data}
      />

      <MessageDialog
        open={messageDialogOpen}
        data={item}
        handleClose={() => {
          setMessageDialogOpen(false);
        }}
        type={type}
        params={params}
        message={{ messageData, setMessageData }}
        user={user}
        setRefetch={setRefetch}
      />

      <StatusDialog
        open={statusDialogOpen}
        handleClose={() => setStatusDialogOpen(false)}
        data={status}
        setDataChanged={setTaskChanged}
      />

      <ColorDialog
        open={colorDialogOpen.open}
        handleClose={() => setColorDialogOpen({ open: false, lineId: null, subMenuUrl: '' })}
        data={colorDialogOpen}
        setKanbanData={setKanbanData}
      />

      <SimpleTaskDeadLineNotificationDialog
        open={notificationDialogOpen}
        handleClose={() => setNotificationDialogOpen(false)}
      />
    </div>
  );
};

export default KanbanView;
